import { Notifier } from "@airbrake/browser";

import { DEV_DOMAIN, LOCAL_DOMAIN } from "@/constants/routes";
import {
  getKubernetesCluster,
  getKubernetesNamespaceAndApp,
  isLocalDev,
} from "@/util/domain";

const hostname = window.location.hostname;

const IS_LOCAL_DEV = isLocalDev(hostname);

const IS_NON_UAT_PROD_ENV =
  [DEV_DOMAIN, LOCAL_DOMAIN].some((url) => hostname.includes(url)) ||
  (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "uat");

const KUBERNETES_ENVIRONMENT = getKubernetesCluster(hostname);
const KUBERNETES_NAMESPACE = getKubernetesNamespaceAndApp(hostname);

// Every notice's environment, with the same keys as LaunchDarkly's context in
// apps/core/src/index.js.
const ENVIRONMENT = {
  environment: KUBERNETES_ENVIRONMENT,
  namespace: KUBERNETES_NAMESPACE,
};

let project = {};
let notifier = null;

// The reports already sent, so each is sent once per page load.
const reported = new Set();

/**
 * configureAirbrake
 *
 * Takes RMD's Airbrake project from the app config's `airbrake` key:
 * apps/core/public/config.json locally, and app-state's service-endpoints.yaml
 * when deployed. apps/core/src/index.js calls it once, before anything reports.
 *
 * @param {{ airbrake?: { projectId?: string, projectKey?: string } }} [config]
 * @returns {void}
 */
export const configureAirbrake = (config) => {
  project = { ...config?.airbrake };
};

const createNotifier = () => {
  const newNotifier = new Notifier({
    projectId: project.projectId,
    projectKey: project.projectKey,
    environment: KUBERNETES_ENVIRONMENT,
    // Hooks into no window errors, requests, navigation, or console output,
    // so it sends only what it's given.
    instrumentation: {
      onerror: false,
      fetch: false,
      history: false,
      console: false,
      xhr: false,
      unhandledrejection: false,
    },
    remoteConfig: false,
  });

  newNotifier.addFilter((notice) => {
    if (IS_NON_UAT_PROD_ENV) return null;

    notice.context.environment = KUBERNETES_ENVIRONMENT;
    return notice;
  });

  return newNotifier;
};

const getNotifier = () => {
  if (!notifier) {
    notifier = createNotifier();
  }

  return notifier;
};

const messageOf = (error) =>
  error instanceof Error ? error.message : String(error);

/**
 * report
 *
 * Sends an error to RMD's Airbrake project, from UAT and production only. It
 * takes the fields of an Airbrake notice, as in @airbrake/browser's README,
 * and fills in `environment` with the page's Kubernetes environment and
 * namespace. A report with the same message, context, and params as one
 * already sent is dropped, so each is sent once per page load. It does nothing
 * on local dev, or when the app config has no Airbrake project, and it never
 * throws or rejects. The notifier is built on the first report; the store and
 * ErrorBoundary keep their own notifiers, which report uncaught errors.
 *
 * @param {object} notice
 * @param {unknown} notice.error What went wrong; the only field needed.
 * @param {Record<string, unknown>} [notice.context] Where it went wrong, like
 *   `{ component: "httpClientFactory" }`.
 * @param {Record<string, unknown>} [notice.params] What helps explain it, like
 *   `{ service: "payment_api" }`.
 * @param {Record<string, unknown>} [notice.session] Not for use yet; nothing
 *   fills it in.
 * @returns {void}
 *
 * @example
 * report({
 *   error: cause,
 *   context: { component: "httpClientFactory" },
 *   params: { service: name },
 * });
 */
export const report = (notice) => {
  const send = async () => {
    const { error, context, params, session } = notice;
    if (IS_LOCAL_DEV || !project.projectId || !project.projectKey) return;

    const reason = JSON.stringify([messageOf(error), context, params]);
    if (reported.has(reason)) return;
    reported.add(reason);

    await getNotifier().notify({
      error,
      context,
      params,
      session,
      environment: ENVIRONMENT,
    });
  };

  send().catch(() => undefined);
};
